import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import FileUpload from './features/transactions/FileUpload';
import PublicLinkModal from './components/PublicLinkModal';
import axios from 'axios';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginSuccessPage from './components/LoginSuccessPage';
import Header from './components/Header';
import DashboardPage from './pages/DashboardPage';
import SummaryPage from './pages/SummaryPage';
import SpendingsPage from './pages/SpendingsPage';
import SchoolYearSummaryPage from './pages/SchoolYearSummaryPage';
import VisibilityManagerPage from './pages/VisibilityManagerPage';
import EventsManagerPage from './pages/EventsManagerPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import PublicSummaryPage from './pages/PublicSummaryPage';
import { Modal, Spinner } from 'react-bootstrap';
import { useTheme } from './theme';
import './api/client';
import { SchoolYear } from './types';

function App() {
    const [theme, toggleTheme] = useTheme();
    return (
        <Routes>
            <Route path="/login-success" element={<LoginSuccessPage />} />
            <Route path="/public/:secret" element={<PublicSummaryPage theme={theme} onToggleTheme={toggleTheme} />} />
            <Route path="/*" element={<MainApp theme={theme} onToggleTheme={toggleTheme} />} />
        </Routes>
    );
}

function MainApp({ theme, onToggleTheme }: { theme: 'light' | 'dark', onToggleTheme: () => void }) {
    const [refresh, setRefresh] = useState(false);
    const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
    const [selectedSchoolYear, setSelectedSchoolYear] = useState<SchoolYear | null>(null);
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [lastUploadTimestamp, setLastUploadTimestamp] = useState<string | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);

    // Derived (not stored in state) so the dates can never lag behind the selected year by a render.
    const startDate = selectedSchoolYear?.startDate ?? null;
    const endDate = selectedSchoolYear?.endDate ?? null;

    // The sidebar layout only applies to logged-in screens; the login card is centred on its own.
    useEffect(() => {
        document.body.classList.toggle('app-shell', !!user);
        return () => {
            document.body.classList.remove('app-shell');
        };
    }, [user]);

    useEffect(() => {
        axios.get('/api/user').then(response => {
            setUser(response.data);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (user) {
            axios.get<string>('/api/settings/last-upload-timestamp').then(response => {
                setLastUploadTimestamp(response.data);
            }).catch(() => {
                setLastUploadTimestamp(null);
            });
        }
    }, [user, refresh]);

    useEffect(() => {
        axios.get<SchoolYear[]>('/api/school-years').then(response => {
            const years = response.data;
            console.log("Fetched school years:", years);
            setSchoolYears(years);
            const currentYear = years.find(y => y.current);
            if (currentYear) {
                setSelectedSchoolYear(currentYear);
                console.log("Selected current year:", currentYear);
            } else {
                console.log("No current year found. Selecting first year:", years[0]);
                setSelectedSchoolYear(years[0]);
            }
        }).catch(error => {
            console.error('Error fetching school years:', error);
        });
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Check the origin of the message for security
            if (event.origin !== window.location.origin) {
                return;
            }

            if (event.data === 'loginSuccess') {
                axios.get('/api/user').then(response => {
                    setUser(response.data);
                }).catch(error => {
                    console.error('Error fetching user after login:', error);
                });
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const handleSchoolYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedYearName = event.target.value;
        const year = schoolYears.find(y => y.name === selectedYearName);
        if (year) {
            setSelectedSchoolYear(year);
        }
    };

    const handleUpload = () => {
        setRefresh(!refresh);
        setShowUploadModal(false);
    };

    const logout = () => {
        axios.post('/logout').then(() => {
            setUser(null);
        });
    }

    const handleUploadClick = () => {
        setShowUploadModal(true);
    };

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleString('pl-PL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="login-hero">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Ładowanie…</span>
                </Spinner>
            </div>
        );
    }

    return (
        <>
            <Header
                onLogout={logout}
                onUploadClick={handleUploadClick}
                onPublicLinkClick={() => setShowPublicLinkModal(true)}
                userName={user?.name}
                schoolYears={schoolYears}
                selectedSchoolYear={selectedSchoolYear}
                handleSchoolYearChange={handleSchoolYearChange}
                lastUploadTimestamp={lastUploadTimestamp}
                formatTimestamp={formatTimestamp}
                theme={theme}
                onToggleTheme={onToggleTheme}
            />
            {user && selectedSchoolYear && (
                <Routes>
                    <Route path="/" element={<Navigate to="/summary" replace />} />
                    <Route path="/dashboard" element={<DashboardPage refresh={refresh} onAssign={handleUpload} startDate={startDate} endDate={endDate} schoolYear={selectedSchoolYear} />} />
                    <Route path="/summary" element={<SummaryPage refresh={refresh} onAssign={handleUpload} startDate={startDate} endDate={endDate} schoolYear={selectedSchoolYear.name} />} />
                    <Route path="/spendings" element={<SpendingsPage startDate={startDate} endDate={endDate} schoolYear={selectedSchoolYear.name} />} />
                    <Route path="/school-year-summary" element={<SchoolYearSummaryPage refresh={refresh} />} />
                    <Route path="/visibility-manager" element={<VisibilityManagerPage schoolYear={selectedSchoolYear.name} onVisibilityChange={handleUpload} />} />
                    <Route path="/events-manager" element={<EventsManagerPage schoolYearId={selectedSchoolYear.id} />} />
                    <Route path="/history" element={<TransactionHistoryPage startDate={startDate} endDate={endDate} schoolYear={selectedSchoolYear.name} />} />
                </Routes>
            )}
            {!user && (
                <div className="login-hero">
                    <div className="login-card surface">
                        <div className="brand-mark" aria-hidden="true">S</div>
                        <h1 className="login-logo">Skarbnik</h1>
                        <p>Aplikacja do zarządzania finansami klasy</p>
                        <button
                            className="google-btn"
                            onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/oauth2/authorization/google`, 'googleLogin', 'width=500,height=600')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Zaloguj przez Google
                        </button>
                        <p className="login-note">Dostęp mają tylko uprawnione konta Google.</p>
                    </div>
                </div>
            )}

            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Wgraj transakcje</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FileUpload onUpload={handleUpload} />
                </Modal.Body>
            </Modal>

            <PublicLinkModal show={showPublicLinkModal} onHide={() => setShowPublicLinkModal(false)} />
        </>
    );
}

export default App;